import { useMemo, useState } from "react";
import {
  GET_OTP_CONTACT,
  getErrorMsg,
  RESEND_OTP,
  SEND_EMAIL_OTP,
  SEND_PHONE_OTP,
  VERIFY_OTP,
} from "@/api/otp";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useHMOStore } from "../../hooks/use-hmo-store";
import {
  safeObject,
  safeString,
  validateEmail,
  validatePhone,
} from "@/lib/utils";
import { ReuseCustomOTP } from "@/components/ui/custom-otp";
import { Mail, MessageCircleMore, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldLabel,
  FieldTitle,
} from "@/components/ui/field";
import { SpinnerLoader } from "@/components/spinner-loader";
import { useWidgetColor } from "@/hooks/useWidgetColor";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Input } from "@/components/ui/input";
import { FormStep } from "@/features/consultation/types";
import { ContactMode, TContact } from "@/lib/typescript/types";
import {
  getContactAvailabilityError,
  maskContactInfo,
  matchesInput,
} from "@/lib/2fa-utils";
import { ExpandInput } from "./expanded-input";

const twoFAModes = safeString(import.meta.env.VITE_APP_2FA_MODE);
const twoFAModesArray = twoFAModes.split(" ");

export function TwoFA({
  backBtn,
  next,
}: {
  backBtn: React.ReactNode;
  next: FormStep;
}) {
  const widgetColor = useWidgetColor();
  const [sentOTP, setSentOTP] = useState(false);
  const [otpMode, setOtpMode] = useState(twoFAModesArray[0]);
  const setFromStep = useHMOStore((s) => s.setFormStep);
  const email = useHMOStore((s) => s.userData)?.email;
  const phoneNumber = useHMOStore((s) => s.userData)?.phone;

  const [customInputs, setCustomInputs] = useState<Record<string, string>>({
    sms: "",
    whatsapp: "",
    email: "",
  });
  const [inputErrors, setInputErrors] = useState<Record<string, string>>({
    sms: "",
    whatsapp: "",
    email: "",
  });

  const formValues = useHMOStore((s) => s.userData);
  const partnerInfo = useHMOStore((s) => s.partnerInfo);
  const hmoId = formValues?.hmoId;
  const providerId = partnerInfo?.providerId;
  const safeHmoId = safeString(hmoId);
  const safeProviderId = safeString(providerId);

  const { data: contactData, isPending: loadingContact } = useQuery({
    queryKey: ["GET_CONTACT"],
    queryFn: () => GET_OTP_CONTACT(safeHmoId, safeProviderId),
    enabled: !!hmoId && !!providerId,
  });

  const contact = useMemo(
    () => safeObject<TContact>(contactData?.data?.data),
    [contactData],
  );

  const {
    mutate: sendOTP,
    isPending: sendingOTP,
    error: sendOtpError,
  } = useMutation({
    mutationKey: ["SEND_OTP"],
    mutationFn: async () => {
      return otpMode === "email"
        ? SEND_EMAIL_OTP(safeHmoId, safeProviderId, otpMode, safeString(email))
        : SEND_PHONE_OTP(
            safeHmoId,
            safeProviderId,
            otpMode,
            safeString(phoneNumber),
          );
    },
    onSuccess: () => setSentOTP(true),
  });

  const {
    mutateAsync: resendOTP,
    isPending: resendingOTP,
    error: resendOtpError,
  } = useMutation({
    mutationKey: ["RESEND_OTP"],
    mutationFn: async () => RESEND_OTP(safeHmoId, safeProviderId),
  });

  const {
    mutateAsync: verifyOTP,
    isPending: verifyingOTP,
    error: verifyOtpError,
  } = useMutation({
    mutationKey: ["VERIFY_OTP"],
    mutationFn: async (otp: string) =>
      VERIFY_OTP(safeHmoId, safeProviderId, otp),
  });

  const contactModes = useMemo<ContactMode[]>(() => {
    // Build the full list first so we know how many modes will be shown —
    // this lets getContactAvailabilityError give the right message.
    const filtered = [
      {
        title: "SMS",
        // Prefer the server-supplied masked value; fall back to masking the
        // raw phone we have from the store.
        info: contact?.maskedPhone || maskContactInfo(phoneNumber),
        value: "sms",
        icon: Phone,
        inputType: "tel" as const,
        placeholder: "Enter your phone number",
        hint: `Must match the number: ${contact?.maskedPhone || maskContactInfo(phoneNumber) || "—"}`,
      },
      {
        title: "WhatsApp",
        info: contact?.maskedPhone || maskContactInfo(phoneNumber),
        value: "whatsapp",
        icon: MessageCircleMore,
        inputType: "tel" as const,
        placeholder: "Enter your WhatsApp number",
        hint: `Must match the number: ${contact?.maskedPhone || maskContactInfo(phoneNumber) || "—"}`,
      },
      {
        title: "Email Address",
        info: contact?.maskedEmail || maskContactInfo(email),
        value: "email",
        icon: Mail,
        inputType: "email" as const,
        placeholder: "Enter your email address",
        hint: `Must match the email: ${contact?.maskedEmail || maskContactInfo(email) || "—"}`,
      },
    ].filter((mode) => twoFAModesArray.includes(mode.value));

    const hasMultiple = filtered.length > 1;

    return filtered.map((mode) => ({
      ...mode,
      unavailable:
        getContactAvailabilityError(
          mode.value,
          email,
          phoneNumber,
          hasMultiple,
        ) !== null,
      // Override hint with availability error when contact is missing/invalid
      hint:
        getContactAvailabilityError(
          mode.value,
          email,
          phoneNumber,
          hasMultiple,
        ) ?? mode.hint,
    }));
  }, [contact, email, phoneNumber]);

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------
  function validateField(mode: string, value: string): string {
    const isEmail = mode === "email";
    const mask = isEmail
      ? contact?.maskedEmail || maskContactInfo(email)
      : contact?.maskedPhone || maskContactInfo(phoneNumber);

    const formatError = isEmail ? validateEmail(value) : validatePhone(value);
    if (formatError) return formatError;

    const isValid = isEmail
      ? matchesInput(value, email)
      : matchesInput(value, phoneNumber);
    if (!isValid) {
      return isEmail
        ? `This email doesn't match: (${mask}).`
        : `This number doesn't match: (${mask}).`;
    }
    return "";
  }

  function handleInputChange(value: string, mode: string) {
    setCustomInputs((prev) => ({ ...prev, [mode]: value }));
    if (inputErrors[mode]) setInputErrors((prev) => ({ ...prev, [mode]: "" }));
  }

  function handleBlur(mode: string) {
    const value = customInputs[mode];
    if (!value) return;
    setInputErrors((prev) => ({ ...prev, [mode]: validateField(mode, value) }));
  }

  function handleSend() {
    const currentMode = contactModes.find((m) => m.value === otpMode);

    // Guard: selected mode has no valid contact — shouldn't normally be
    // reachable since the input is disabled, but defensive check.
    if (currentMode?.unavailable) return;

    const err = validateField(otpMode, customInputs[otpMode]);
    if (err) {
      setInputErrors((prev) => ({ ...prev, [otpMode]: err }));
      return;
    }
    sendOTP();
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div className="flex flex-col gap-4 mt-4">
      {backBtn}

      {loadingContact ? (
        <div className="mx-auto">
          <SpinnerLoader />
        </div>
      ) : (
        <div>
          <h5 className="font-medium text-base text-[#0C1322] leading-6">
            Verify your identity
          </h5>

          {!sentOTP && (
            <div className="space-y-4">
              <p className="text-[#5D626C] text-sm lg:text-base leading-6">
                Select where you'd like to receive your OTP, then confirm your
                contact details below.
              </p>

              <div className="w-full space-y-3">
                <RadioGroup
                  defaultValue={otpMode}
                  value={otpMode}
                  className="w-full space-y-3"
                  onValueChange={(val) => setOtpMode(val)}
                >
                  {contactModes.map((item, idx) => {
                    const id = item.value + idx;
                    const isSelected = item.value === otpMode;
                    const hasInputError = !!inputErrors[item.value];
                    // A mode is "broken" when its contact info is missing/invalid.
                    const isUnavailable = item.unavailable;

                    return (
                      <div key={id}>
                        {/* Option row */}
                        <FieldLabel
                          htmlFor={id}
                          style={{
                            borderColor: isUnavailable
                              ? "#FCA5A5"
                              : isSelected
                                ? widgetColor
                                : "#F3F4F6",
                            backgroundColor: isUnavailable
                              ? "#FEF2F2"
                              : isSelected
                                ? `${widgetColor}08`
                                : "transparent",
                            borderRadius: "8px",
                            borderWidth: "1.5px",
                            cursor: isUnavailable ? "not-allowed" : "pointer",
                            opacity: isUnavailable ? 0.7 : 1,
                            transition:
                              "border-color 0.2s, background-color 0.2s",
                          }}
                        >
                          <Field orientation="horizontal">
                            <FieldContent className="min-w-0 flex-1">
                              <div className="flex items-center space-x-3 sm:space-x-4">
                                {/* Icon */}
                                <div
                                  style={{
                                    backgroundColor: isUnavailable
                                      ? "#FEE2E2"
                                      : isSelected
                                        ? `${widgetColor}22`
                                        : "#F3F4F6",
                                    transition: "background-color 0.2s",
                                  }}
                                  className="shrink-0 rounded-full p-1.5 sm:p-2"
                                >
                                  <item.icon
                                    size={16}
                                    className="sm:hidden"
                                    color={
                                      isUnavailable
                                        ? "#EF4444"
                                        : isSelected
                                          ? widgetColor
                                          : "#5D626C"
                                    }
                                  />
                                  <item.icon
                                    size={18}
                                    className="hidden sm:block"
                                    color={
                                      isUnavailable
                                        ? "#EF4444"
                                        : isSelected
                                          ? widgetColor
                                          : "#5D626C"
                                    }
                                  />
                                </div>

                                {/* Text */}
                                <div className="min-w-0">
                                  <FieldTitle className="text-sm sm:text-base">
                                    {item.title}
                                  </FieldTitle>
                                  <FieldDescription className="truncate text-xs sm:text-sm sm:whitespace-normal">
                                    {item?.info}
                                  </FieldDescription>
                                </div>
                              </div>
                            </FieldContent>

                            {/* Radio dot */}
                            <RadioGroupItem
                              className="my-auto ml-2 shrink-0"
                              value={item.value}
                              id={id}
                              // Prevent selecting an option with no valid contact.
                              disabled={isUnavailable}
                            />
                          </Field>
                        </FieldLabel>

                        {/* Inline expanding section */}
                        <ExpandInput open={isSelected}>
                          <div className="pt-1">
                            {isUnavailable ? (
                              // ── Availability error banner ──────────────────
                              <p className="mt-1 px-1 text-xs sm:text-sm text-red-500">
                                {item.hint}
                              </p>
                            ) : (
                              // ── Normal confirmed-contact input ─────────────
                              <>
                                <Input
                                  id={`input-${item.value}`}
                                  type={item.inputType}
                                  value={customInputs[item.value]}
                                  onChange={(e) =>
                                    handleInputChange(
                                      e.target.value,
                                      item.value,
                                    )
                                  }
                                  onBlur={() => handleBlur(item.value)}
                                  placeholder={item.placeholder}
                                  autoComplete="off"
                                  className="w-full text-sm sm:text-base"
                                />
                                <p
                                  className="mt-1.5 px-1 text-xs sm:text-sm"
                                  style={{
                                    color: hasInputError
                                      ? "#EF4444"
                                      : "#5D626C",
                                  }}
                                >
                                  {hasInputError
                                    ? inputErrors[item.value]
                                    : item.hint}
                                </p>
                              </>
                            )}
                          </div>
                        </ExpandInput>
                      </div>
                    );
                  })}
                </RadioGroup>
              </div>

              {sendOtpError && (
                <p className="text-red-500 text-sm">
                  {getErrorMsg(sendOtpError)}
                </p>
              )}

              <Button
                className="w-full"
                // Also disable when the currently selected mode is unavailable.
                disabled={
                  sendingOTP ||
                  (contactModes.find((m) => m.value === otpMode)?.unavailable ??
                    false)
                }
                onClick={handleSend}
                style={{ backgroundColor: widgetColor }}
                loading={sendingOTP}
              >
                Send code
              </Button>
            </div>
          )}

          {sentOTP && (
            <div className="space-y-3">
              <div className="text-[#5D626C] text-sm lg:text-base leading-6">
                <p>
                  We sent a 6-digit code to{" "}
                  {otpMode === "email"
                    ? contact?.maskedEmail || maskContactInfo(email)
                    : contact?.maskedPhone || maskContactInfo(phoneNumber)}
                  . Please enter it below:
                </p>
              </div>

              <ReuseCustomOTP
                verify={{
                  loading: verifyingOTP,
                  fn: async (otp) => {
                    try {
                      await verifyOTP(otp);
                      setFromStep(next);
                      return true;
                    } catch {
                      return false;
                    }
                  },
                  error: verifyOtpError
                    ? getErrorMsg(verifyOtpError)
                    : undefined,
                }}
                resend={{
                  loading: resendingOTP,
                  fn: async () => {
                    await resendOTP();
                    if (resendOtpError) {
                      return false;
                    } else {
                      return true;
                    }
                  },
                  error: resendOtpError
                    ? getErrorMsg(resendOtpError)
                    : undefined,
                }}
              />
              {twoFAModesArray?.length > 1 && (
                <div className="text-center text-[14px] text-[#5D626C] flex items-center space-x-1">
                  <button
                    style={{ color: widgetColor }}
                    className="underline cursor-pointer"
                    onClick={() => setSentOTP(false)}
                  >
                    Click here
                  </button>{" "}
                  <p>to select another way you will want to receive the OTP.</p>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
